import React, { FC, useCallback, useState } from 'react';
import { Route, Switch, useHistory, useParams, useRouteMatch } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  PopoverBody,
  Row,
  Table,
  UncontrolledPopover,
} from 'reactstrap';
import { RoleInvite } from '../../../../shared/user/roles';
import { DateTime, LinkButton, MaterialIcon } from '../../../components';
import { NullableTimeZoneInputField } from '../../../components/TimeZoneInputField';
import { Role } from '../../../models';
import { useTranslation } from '../../../translations';
import { Fetcher, useConvention, useFetcher, useForm, useNumericInputState } from '../../../utils';

interface RoleInviteProps {
  readonly role: Role;
}

export const RoleInvites: FC<RoleInviteProps> = ({ role }) => {
  const match = useRouteMatch();

  return (
    <Switch>
      <Route exact path={match.path}>
        <RoleInviteList role={role} />
      </Route>
      <Route exact path={`${match.path}/create`}>
        <CreateRoleInviteForm role={role} />
      </Route>
      <Route exact path={`${match.path}/:id/edit`}>
        <EditRoleInviteForm role={role} />
      </Route>
    </Switch>
  );
};

const RoleInviteList: FC<RoleInviteProps> = ({ role }) => {
  const { ts } = useTranslation();
  const match = useRouteMatch();
  const domainAndProtocol = window.location.origin;

  const fetcher = useFetcher(async () => {
    return await api.getRoleInvites(role.id);
  }, [role.id]);

  const copyCode = useCallback(
    async (code: string) => {
      await navigator.clipboard.writeText(`${domainAndProtocol}/roles/invite/${code}`);
      toast.success(ts('invite_link_copied'));
    },
    [domainAndProtocol, ts],
  );

  if (role.name === 'lead' || role.name === 'dealerAssistant') {
    return <p>{ts('internal_role_no_invites')}</p>;
  }

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <>
      <Row>
        <Col md={12}>
          <LinkButton className="mb-3" id="createRoleInvite" to={`${match.url}/create`}>
            {ts('create_invite')}
          </LinkButton>
        </Col>
      </Row>
      <Table id="roleInviteTable">
        <thead>
          <tr>
            <th style={{ width: '15%' }}>{ts('common.code')}</th>
            <th>{ts('expiration')}</th>
            <th style={{ width: '10%' }}>{ts('uses')}</th>
            <th>{ts('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {fetcher.data.map((invite) => {
            const total = `${invite.uses} / ${invite.maxUses ?? 0}`;
            const expired = invite.expiresAt && invite.expiresAt < new Date();
            const full = invite.maxUses && invite.uses >= invite.maxUses;

            return (
              <tr key={invite.id}>
                <td>{invite.code}</td>
                <td>
                  {invite.expiresAt ? <DateTime value={invite.expiresAt} /> : ts('no_expiration')}
                  {expired && (
                    <Badge color="danger" id={`${role.id}Expired`} pill>
                      {ts('vouchers.expired')}
                    </Badge>
                  )}
                </td>
                <td>{invite.maxUses ? total : ts('unlimited')}</td>
                <td>
                  <LinkButton
                    className="btn-sm ml-2"
                    color="primary"
                    id={`editInvite${invite.id}`}
                    to={`${match.url}/${invite.id}/edit`}
                  >
                    {ts('common.edit')}
                  </LinkButton>
                  {!full && !expired && (
                    <Button
                      className="btn-sm ml-2"
                      color="primary"
                      onClick={async () => {
                        await copyCode(invite.code);
                      }}
                    >
                      {ts('copy_link')}
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </>
  );
};

interface EditRoleInviteFormProps {
  readonly role: Role;
}

const EditRoleInviteForm: FC<EditRoleInviteFormProps> = ({ role }) => {
  const inviteId = useParams<{ id: string }>().id;
  const fetcher = useFetcher(async () => {
    return await api.getRoleInvite(role.id, Number.parseInt(inviteId, 10));
  }, [inviteId, role.id]);

  if (!fetcher.success) {
    return <Fetcher result={fetcher} />;
  }

  return <CreateRoleInviteForm invite={fetcher.data} role={role} />;
};

interface CreateRoleInviteFormProps {
  readonly role: Role;
  readonly invite?: RoleInvite;
}

const CreateRoleInviteForm: FC<CreateRoleInviteFormProps> = ({ role, invite }) => {
  const { ts } = useTranslation();
  const { timeZone } = useConvention();
  const history = useHistory();
  const rolePage = `/housekeeping/roles/${role.id}/invites`;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const [amount, setAmount] = useNumericInputState(1);
  const [maxUses, setMaxUses] = useNumericInputState(invite?.maxUses ?? 1);
  const [expiresAt, setExpiresAt] = useState<Date | null>(invite?.expiresAt ?? tomorrow);

  const form = useForm(async () => {
    if (invite?.id) {
      await api.updateRoleInvite(role.id, invite.id, {
        maxUses: maxUses === -1 ? null : maxUses,
        expiresAt,
      });
    } else {
      await api.createRoleInvites(role.id, {
        amount,
        maxUses: maxUses === -1 ? null : maxUses,
        expiresAt,
      });

      toast.success(ts('invites_created'));
    }

    history.push(rolePage);
  }, [invite?.id, history, rolePage, role.id, maxUses, expiresAt, amount, ts]);

  return (
    <>
      <h3>{ts(invite ? 'common.edit' : 'create_invite_submit')}</h3>
      {!invite && <p>{ts('create_invite_submit_description')}</p>}
      <Form id="createInviteForm" onSubmit={form.onSubmit}>
        <Row>
          <Col lg={3} xs={12}>
            <FormGroup>
              {invite ? (
                <>
                  <Label for="code">{ts('common.code')}</Label>
                  <Input disabled id="code" value={invite.code} />
                </>
              ) : (
                <>
                  <Label for="amount">{ts('common.amount')}</Label>
                  <Input
                    id="amount"
                    onChange={setAmount}
                    placeholder={ts('common.amount')}
                    required
                    type="number"
                    value={amount}
                  />
                </>
              )}
            </FormGroup>
          </Col>
          <Col lg={3} xs={12}>
            <FormGroup>
              <Label for="amount">
                {ts('vouchers.max_uses')}
                <MaterialIcon id="amountPopover" name="help" style={{ fontSize: '1rem' }} />
              </Label>
              <UncontrolledPopover target="amountPopover" trigger="hover">
                <PopoverBody>{ts('vouchers.unlimited_uses')}</PopoverBody>
              </UncontrolledPopover>
              <Input
                id="maxUses"
                min={-1}
                onChange={setMaxUses}
                placeholder={ts('vouchers.max_uses')}
                required
                type="number"
                value={maxUses}
              />
            </FormGroup>
          </Col>
          <Col lg={6} xs={12}>
            <FormGroup>
              <Label for="expiresAt">{ts('vouchers.expires')}</Label>
              <NullableTimeZoneInputField
                defaultValue={expiresAt}
                id="expiresAt"
                onChange={setExpiresAt}
                required
                timeZone={timeZone}
              />
            </FormGroup>
          </Col>
        </Row>
        <Row className="justify-content-center">
          <Col lg={8} xs={12}>
            <Button
              block
              color="primary"
              disabled={form.saving}
              id="submitRoleInvite"
              type="submit"
            >
              {ts(invite ? 'common.save' : 'create_invite')}
            </Button>
          </Col>
        </Row>
      </Form>
    </>
  );
};
